// apps/web/src/features/russia-requests/components/RussiaRequestDrawerHeader.tsx
//
// Request-workspace drawer header for `/russia-rates/:id` (#972). Mirrors
// the China `RequestDrawerHeader` rhythm: id/status/type row, title h2,
// route+createdAt meta row, inline transition actions (desktop only)
// plus a quiet close icon button. The narrow surface drops the inline
// actions; the sticky action bar mounted by the drawer body takes over.
//
// WHY: parity with the China shell so users moving between
//   `/china-rates` and `/russia-rates` see a single visual rhythm.
//   See `apps/web/docs/request-workspace-ui-contract.md` for the
//   request-workspace contract.

import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RussiaRequestKind, type RussiaRequest } from '@magimp/shared';

import { Icon } from '../../../ui/index.js';
import { formatDateTime } from '../../../i18n/index.js';
import { RequestStatusPill } from '../../requests/display/index.js';
import type { RussiaTransitionAction } from '../role.js';
import { RussiaRequestTypeChip } from './RussiaRequestTypeChip.js';
import {
  RussiaRequestDrawerHeaderActions,
  type RussiaDuplicateButtonProps,
} from './RussiaRequestDrawerHeaderActions.js';

function fmtNumber(value: number | null): string {
  return value === null ? '—' : String(value);
}

function sumNullable(values: (number | null)[]): string {
  // WHY: only sum the non-null contributors; if every value is null → '—'
  //   (not 0, which would be misleading — no data vs. actual zero weight).
  //   Round to 4 decimal places to strip IEEE 754 float artifacts
  //   (e.g. 0.1 + 0.2 → 0.30000000000000004) while preserving realistic
  //   cargo precision (kg/m³ values never need more than 4 dp in practice).
  const nonNull = values.filter((v): v is number => v !== null);
  if (nonNull.length === 0) return '—';
  const total = nonNull.reduce((a, b) => a + b, 0);
  return String(Math.round(total * 1e4) / 1e4);
}

function TotalsCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          letterSpacing: '.08em',
          textTransform: 'uppercase' as const,
          fontWeight: 600,
          color: 'var(--fg-3)',
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--fg-1)',
          fontVariantNumeric: 'tabular-nums',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {value}
      </div>
    </div>
  );
}

interface Props {
  request: RussiaRequest;
  onClose: () => void;
  narrow: boolean;
  actions: RussiaTransitionAction[];
  onAction: (a: RussiaTransitionAction) => void;
  pending: boolean;
  duplicate: RussiaDuplicateButtonProps;
  /**
   * #1064 UI defense-in-depth: when `false`, the parent-link chip is
   * suppressed even if `request.parentRequestId` is populated. Computed
   * in the drawer body via `canSeeChinaParentFromRussia(permissions)`.
   * With #1063 landed, `parentRequestId` is already `null` on the wire
   * for logist_ru — this flag is the belt the wire-side suspenders ride
   * on, mandated by #1064 so a future backend regression cannot leak
   * the link into the UI.
   */
  canSeeChinaParent: boolean;
  /**
   * #1571: show the complete-draft edit affordance. Computed in the drawer
   * body as draft-like status AND `russiaRequests.manage`. Hidden otherwise.
   */
  canEdit: boolean;
  /** Navigates to the draft editor at `/russia-rates/:id/edit`. */
  onEdit: () => void;
}

export function RussiaRequestDrawerHeader({
  request,
  onClose,
  narrow,
  actions,
  onAction,
  pending,
  duplicate,
  canSeeChinaParent,
  canEdit,
  onEdit,
}: Props) {
  const { t } = useTranslation(['russiaRequests', 'common']);
  const address = request.endAddress || t('russiaRequests:drawer.header.emptyAddress');
  const createdAtText = t('russiaRequests:drawer.header.createdAt', {
    date: formatDateTime(request.createdAt),
  });

  return (
    <header
      data-testid="russia-request-drawer-header"
      style={{
        padding: '16px 24px 0',
        borderBottom: '1px solid var(--border-1)',
        flex: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        <div
          data-testid="russia-request-drawer-header-text"
          style={{ flex: '1 1 320px', minWidth: 0 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 6,
              flexWrap: 'wrap',
            }}
          >
            <span
              data-testid="russia-request-drawer-header-id"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--fg-3)',
              }}
            >
              {request.id}
            </span>
            <RequestStatusPill status={request.status} />
            <RussiaRequestTypeChip type={request.type} />
            {request.parentRequestId !== null && canSeeChinaParent && (
              <Link
                // WHY (#940): the china drawer is query-string driven (see
                //   `useRequestUrlState`), not path-segment. The AC mentions
                //   `/china-rates/{parentId}` as shorthand, but the routed
                //   contract is `?openId=...&tab=...`. `tab=carriers` mirrors
                //   the china default so the link lands on the most useful
                //   sub-surface (bids).
                to={`/china-rates?openId=${encodeURIComponent(request.parentRequestId)}&tab=carriers`}
                data-testid="russia-request-drawer-parent-link"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  borderRadius: 'var(--r-1)',
                  fontSize: 12,
                  lineHeight: '16px',
                  background: 'var(--bg-2)',
                  color: 'var(--fg-2)',
                  textDecoration: 'none',
                  border: '1px solid var(--border-1)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('russiaRequests:drawer.parentLink', { id: request.parentRequestId })}
              </Link>
            )}
          </div>
          <h2
            data-testid="russia-request-drawer-header-title"
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              color: 'var(--fg-1)',
              letterSpacing: '-.005em',
              lineHeight: 1.25,
            }}
          >
            {request.name}
          </h2>
          <div
            data-testid="russia-request-drawer-header-meta"
            style={{
              fontSize: 12,
              color: 'var(--fg-3)',
              margin: '4px 0 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Icon name="map-pin" size={12} />
            <span
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {address}
            </span>
            <span>·</span>
            <span style={{ whiteSpace: 'nowrap' }}>{createdAtText}</span>
          </div>
          <div
            data-testid="russia-request-drawer-header-totals"
            style={{
              display: 'flex',
              gap: 24,
              margin: '0 0 12px',
              flexWrap: 'wrap',
            }}
          >
            {request.kind === RussiaRequestKind.SIMPLE ? (
              <>
                <TotalsCell
                  label={t('russiaRequests:drawer.cargo.totals.weightKg')}
                  value={sumNullable(request.items.map((it) => it.weightKg))}
                />
                <TotalsCell
                  label={t('russiaRequests:drawer.cargo.totals.volumeM3')}
                  value={sumNullable(request.items.map((it) => it.volumeM3))}
                />
                <TotalsCell
                  label={t('russiaRequests:drawer.cargo.totals.quantity')}
                  value={
                    request.items.length === 0
                      ? '—'
                      : String(request.items.reduce((a, it) => a + it.quantity, 0))
                  }
                />
              </>
            ) : (
              <>
                <TotalsCell
                  label={t('russiaRequests:drawer.cargo.totals.weightKg')}
                  value={fmtNumber(request.totalWeight)}
                />
                <TotalsCell
                  label={t('russiaRequests:drawer.cargo.totals.volumeM3')}
                  value={fmtNumber(request.totalVolume)}
                />
              </>
            )}
          </div>
        </div>
        <RussiaRequestDrawerHeaderActions
          actions={actions}
          narrow={narrow}
          pending={pending}
          onAction={onAction}
          onClose={onClose}
          canEdit={canEdit}
          onEdit={onEdit}
          duplicate={duplicate}
        />
      </div>
    </header>
  );
}
